import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, UserCheck, X } from 'lucide-react';

/**
 * Cascading "Bağlı Olduğu Amir" seçici.
 *
 * Trigger: kapalı durumda seçili amirin adı + küçük rol badge'i (ad + rol yan yana).
 * İçerik: dropdown açılınca rollerin listesi; bir rolün üzerine gelince (hover/focus)
 *   sağ tarafta o roldeki personellerin alt menüsü açılır.
 * Boş rol: disabled (yanında "(0)").
 *
 * Pre-fill: modal açıldığında `value` doluysa trigger ad + rol badge gösterir;
 * dropdown açılırsa kullanıcı zincirde gezerek değiştirebilir.
 */
const SupervisorPicker = ({
  value,                 // parent_personnel_id (string) | null
  onChange,              // (newValue: string | null) => void
  eligibleSupervisors,   // Aktif, hiyerarşi seviyesi uygun adaylar (PersonnelForm/Page'deki useMemo'dan gelir)
  getRoleById,
  getRoleBySlug,
  getRoleLabel,
  disabled = false,
  placeholder = 'Amir seçin',
  className = '',
}) => {
  // Rol bazlı gruplama — hierarchy_level azalan (en üst rol en üstte)
  const groupedByRole = useMemo(() => {
    const map = new Map(); // role_id -> { roleDef, people: [] }
    (eligibleSupervisors || []).forEach((p) => {
      const roleDef =
        (p.role_id && getRoleById?.(p.role_id)) ||
        (p.role && getRoleBySlug?.(p.role)) ||
        null;
      if (!roleDef) return; // bilinmeyen rol — atla
      const key = roleDef.id;
      if (!map.has(key)) {
        map.set(key, { roleDef, people: [] });
      }
      map.get(key).people.push(p);
    });
    // Hiyerarşik sıralama — üstten alta
    return Array.from(map.values()).sort(
      (a, b) =>
        Number(b.roleDef.hierarchy_level ?? 0) -
        Number(a.roleDef.hierarchy_level ?? 0),
    );
  }, [eligibleSupervisors, getRoleById, getRoleBySlug]);

  const selectedPerson = useMemo(
    () => (eligibleSupervisors || []).find((p) => p.id === value) || null,
    [eligibleSupervisors, value],
  );

  const selectedRoleLabel = useMemo(() => {
    if (!selectedPerson) return null;
    return getRoleLabel?.(selectedPerson.role_id || selectedPerson.role) || null;
  }, [selectedPerson, getRoleLabel]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`w-full justify-between font-normal h-10 ${className}`}
        >
          <span className="flex items-center gap-2 min-w-0 overflow-hidden">
            {selectedPerson ? (
              <>
                <UserCheck className="h-4 w-4 text-purple-600 shrink-0" />
                <span className="truncate">{selectedPerson.name}</span>
                {selectedRoleLabel && (
                  <Badge
                    variant="secondary"
                    className="ml-1 px-1.5 py-0 text-[10px] font-medium bg-purple-100 text-purple-900 border border-purple-200"
                  >
                    {selectedRoleLabel}
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[240px] max-h-72"
        align="start"
      >
        {groupedByRole.length === 0 ? (
          <div className="px-3 py-4 text-xs text-slate-500 text-center">
            Bu rolün üstünde uygun aktif personel yok
          </div>
        ) : (
          <>
            {groupedByRole.map(({ roleDef, people }) => {
              const hasPeople = people.length > 0;
              return (
                <DropdownMenuSub key={roleDef.id}>
                  <DropdownMenuSubTrigger
                    disabled={!hasPeople}
                    className={!hasPeople ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    <span className="flex-1 truncate">
                      {roleDef.display_name || roleDef.slug}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({people.length})
                    </span>
                  </DropdownMenuSubTrigger>
                  {hasPeople && (
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent className="min-w-[200px] max-h-72 overflow-y-auto">
                        {people
                          .slice()
                          .sort((a, b) =>
                            String(a.name || '').localeCompare(String(b.name || ''), 'tr'),
                          )
                          .map((p) => (
                            <DropdownMenuItem
                              key={p.id}
                              onSelect={() => onChange?.(p.id)}
                              className={
                                p.id === value
                                  ? 'bg-purple-50 text-purple-900 font-medium'
                                  : ''
                              }
                            >
                              <span className="truncate">{p.name}</span>
                            </DropdownMenuItem>
                          ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  )}
                </DropdownMenuSub>
              );
            })}
            {value && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => onChange?.(null)}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Amir bağlantısını kaldır
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SupervisorPicker;
